import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/App';
import { Plus, Calendar, CheckCircle, XCircle, Trash2, AlertTriangle, Edit, X } from 'lucide-react';

const MONTH_LABELS_ID = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
];

const formatMonthKey = (key) => {
  if (!key || typeof key !== 'string' || key.length < 7) return key;
  const year = key.slice(0, 4);
  const monthIdx = parseInt(key.slice(5, 7), 10) - 1;
  if (monthIdx < 0 || monthIdx > 11) return key;
  return `${MONTH_LABELS_ID[monthIdx]} ${year}`;
};

const buildMonthOptions = () => {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let y = currentYear - 1; y <= currentYear + 1; y += 1) {
    for (let m = 1; m <= 12; m += 1) {
      const key = `${y}-${String(m).padStart(2, '0')}`;
      options.push({ key, label: `${MONTH_LABELS_ID[m - 1]} ${y}` });
    }
  }
  return options;
};

const emptyForm = { name: '', months: [], default_operational_cost: 0 };

const SettingsPeriod = () => {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Create / Edit dialog
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [monthPicker, setMonthPicker] = useState('');

  // Close period confirmation
  const [closeConfirmDialogOpen, setCloseConfirmDialogOpen] = useState(false);
  const [periodToClose, setPeriodToClose] = useState(null);
  const [nextPeriodId, setNextPeriodId] = useState('');

  const allMonthOptions = useMemo(() => buildMonthOptions(), []);

  useEffect(() => {
    fetchPeriods();
  }, []);

  const fetchPeriods = async () => {
    try {
      const response = await api.get('/periods');
      setPeriods(response.data);
    } catch (error) {
      console.error('Failed to fetch periods:', error);
      toast.error('Gagal memuat data periode');
    } finally {
      setLoading(false);
    }
  };

  // Months already used by ANY period (excluding the one we're editing).
  const usedMonthsByOthers = useMemo(() => {
    const used = new Set();
    periods.forEach((p) => {
      if (editingPeriod && p.id === editingPeriod.id) return;
      (p.months || []).forEach((m) => used.add(m));
    });
    return used;
  }, [periods, editingPeriod]);

  const openCreateDialog = () => {
    setEditingPeriod(null);
    // New period inherits the operational cost of the current active period (editable)
    const prevActive = periods.find((p) => !p.is_closed);
    const inheritedCost = prevActive && prevActive.default_operational_cost != null
      ? prevActive.default_operational_cost
      : 0;
    setFormData({ ...emptyForm, default_operational_cost: inheritedCost });
    setMonthPicker('');
    setFormDialogOpen(true);
  };

  const openEditDialog = (period) => {
    setEditingPeriod(period);
    setFormData({ name: period.name, months: [...(period.months || [])], default_operational_cost: period.default_operational_cost || 0 });
    setMonthPicker('');
    setFormDialogOpen(true);
  };

  const addMonth = (monthKey) => {
    if (!monthKey) return;
    if (formData.months.includes(monthKey)) {
      toast.error('Bulan sudah ada di periode ini');
      return;
    }
    if (usedMonthsByOthers.has(monthKey)) {
      toast.error(`${formatMonthKey(monthKey)} sudah digunakan pada periode lain`);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      months: [...prev.months, monthKey].sort(),
    }));
    setMonthPicker('');
  };

  const removeMonth = (monthKey) => {
    setFormData((prev) => ({
      ...prev,
      months: prev.months.filter((m) => m !== monthKey),
    }));
  };

  const handleSubmitPeriod = async () => {
    if (!formData.name.trim()) {
      toast.error('Nama periode wajib diisi');
      return;
    }
    if (!formData.months || formData.months.length === 0) {
      toast.error('Minimal pilih 1 bulan untuk periode ini');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { name: formData.name.trim(), months: formData.months, default_operational_cost: parseFloat(formData.default_operational_cost) || 0 };
      if (editingPeriod) {
        await api.put(`/periods/${editingPeriod.id}`, payload);
        toast.success(`Periode "${payload.name}" berhasil diperbarui`);
      } else {
        await api.post('/periods', payload);
        toast.success(`Periode "${payload.name}" berhasil dibuat`);
      }
      setFormDialogOpen(false);
      setEditingPeriod(null);
      setFormData(emptyForm);
      setMonthPicker('');
      fetchPeriods();
      window.dispatchEvent(new CustomEvent('periodsUpdated'));
    } catch (error) {
      console.error('Failed to save period:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan periode');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePeriod = async (periodId, periodName) => {
    if (!window.confirm(`Apakah Anda yakin ingin menghapus periode "${periodName}"? Tindakan ini tidak dapat dibatalkan.`)) {
      return;
    }
    try {
      await api.delete(`/periods/${periodId}`);
      toast.success('Periode berhasil dihapus');
      fetchPeriods();
      window.dispatchEvent(new CustomEvent('periodsUpdated'));
    } catch (error) {
      console.error('Failed to delete period:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus periode');
    }
  };

  const handleClosePeriod = async (periodId, periodName) => {
    const availableNextPeriods = periods.filter((p) => !p.is_closed && p.id !== periodId);
    if (availableNextPeriods.length === 0) {
      toast.error('Tidak ada periode berikutnya yang tersedia. Buat periode baru terlebih dahulu sebelum menutup buku.');
      return;
    }
    setPeriodToClose({ id: periodId, name: periodName });
    setNextPeriodId(availableNextPeriods[0].id);
    setCloseConfirmDialogOpen(true);
  };

  const confirmClosePeriod = async () => {
    if (!periodToClose || !nextPeriodId) {
      toast.error('Silakan pilih periode berikutnya');
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post(`/periods/${periodToClose.id}/close`, {
        next_period_id: nextPeriodId,
      });
      toast.success(`Periode "${periodToClose.name}" berhasil ditutup`);
      const result = response.data;
      if (result.transactions_created) {
        const { mitra, investor, invest } = result.transactions_created;
        toast.info(`Transaksi dibuat: Mitra (${mitra}), Investor (${investor}), Hasil Invest (${invest})`, { duration: 5000 });
      }
      setCloseConfirmDialogOpen(false);
      setPeriodToClose(null);
      setNextPeriodId('');
      fetchPeriods();
      window.dispatchEvent(new CustomEvent('periodsUpdated'));
    } catch (error) {
      console.error('Failed to close period:', error);
      toast.error(error.response?.data?.detail || 'Gagal menutup periode');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatMonthsList = (months) => {
    if (!months || months.length === 0) return '-';
    return months.map(formatMonthKey).join(', ');
  };

  const activePeriods = periods.filter((p) => !p.is_closed);
  const closedPeriods = periods.filter((p) => p.is_closed);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Kelola Periode</h1>
          <p className="text-slate-600 mt-2">Kelola periode akuntansi dan tutup buku</p>
        </div>
        <Button
          className="bg-teal-600 hover:bg-teal-700"
          onClick={openCreateDialog}
          disabled={activePeriods.length >= 2}
          data-testid="open-create-period-btn"
        >
          <Plus size={20} className="mr-2" />
          Buat Periode Baru
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Calendar className="text-blue-600 mt-1" size={20} />
            <div className="flex-1">
              <p className="text-sm text-blue-900 font-medium">Informasi Periode</p>
              <p className="text-sm text-blue-700 mt-1">
                • Setiap periode memiliki nama kustom dan satu atau lebih bulan yang tercakup<br />
                • Maksimal 2 periode aktif dapat berjalan bersamaan<br />
                • Bulan yang sudah dipakai oleh periode lain tidak dapat dipakai ulang<br />
                • Tutup buku akan mengunci seluruh bulan dalam periode tersebut sekaligus
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Active Periods */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="text-green-600" size={24} />
                Periode Aktif ({activePeriods.length}/2)
              </CardTitle>
              <CardDescription>Periode yang sedang berjalan dan dapat menerima transaksi</CardDescription>
            </CardHeader>
            <CardContent>
              {activePeriods.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar size={48} className="mx-auto mb-3 opacity-20" />
                  <p>Belum ada periode aktif</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nama Periode</TableHead>
                      <TableHead>Bulan</TableHead>
                      <TableHead>Biaya OP Default</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activePeriods.map((period) => (
                      <TableRow key={period.id} data-testid={`period-row-${period.id}`}>
                        <TableCell className="font-medium">
                          {period.name}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(period.months && period.months.length > 0)
                              ? period.months.map((m) => (
                                <Badge key={m} variant="outline" className="text-xs">{formatMonthKey(m)}</Badge>
                              ))
                              : <span className="text-slate-400 text-xs">-</span>}
                          </div>
                        </TableCell>
                        <TableCell data-testid={`period-op-cost-${period.id}`}>
                          Rp.&nbsp;{Number(period.default_operational_cost || 0).toLocaleString('id-ID')}/gr
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-800 border-green-300">Aktif</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(period)}
                              data-testid={`edit-period-btn-${period.id}`}
                            >
                              <Edit size={16} className="mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeletePeriod(period.id, period.name)}
                              className="border-red-600 text-red-600 hover:bg-red-50"
                              data-testid={`delete-period-btn-${period.id}`}
                            >
                              <Trash2 size={16} className="mr-1" />
                              Hapus
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleClosePeriod(period.id, period.name)}
                              className="border-orange-600 text-orange-600 hover:bg-orange-50"
                              data-testid={`close-period-btn-${period.id}`}
                            >
                              Tutup Buku
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Closed Periods */}
          {closedPeriods.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <XCircle className="text-slate-600" size={24} />
                  Periode Tertutup ({closedPeriods.length})
                </CardTitle>
                <CardDescription>Periode yang telah ditutup dan data tidak dapat diubah</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nama Periode</TableHead>
                      <TableHead>Bulan</TableHead>
                      <TableHead>Biaya OP Default</TableHead>
                      <TableHead>Tanggal Tutup</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {closedPeriods.map((period) => (
                      <TableRow key={period.id}>
                        <TableCell className="font-medium">
                          {period.name}
                        </TableCell>
                        <TableCell className="text-sm">{formatMonthsList(period.months)}</TableCell>
                        <TableCell data-testid={`period-op-cost-${period.id}`}>
                          Rp.&nbsp;{Number(period.default_operational_cost || 0).toLocaleString('id-ID')}/gr
                        </TableCell>
                        <TableCell>{formatDate(period.end_date)}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="bg-slate-100 text-slate-700">Tertutup</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Create / Edit Period Dialog */}
      <Dialog
        open={formDialogOpen}
        onOpenChange={(o) => {
          setFormDialogOpen(o);
          if (!o) {
            setEditingPeriod(null);
            setFormData(emptyForm);
            setMonthPicker('');
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingPeriod ? 'Edit Periode' : 'Buat Periode Baru'}
            </DialogTitle>
            <DialogDescription>
              Tentukan nama periode dan pilih satu atau lebih bulan yang termasuk di dalamnya.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="period-name">Nama Periode *</Label>
              <Input
                id="period-name"
                data-testid="period-name-input"
                placeholder="Contoh: Q1 2026 atau Januari-Maret 2026"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Bulan Periode *</Label>
              <p className="text-xs text-slate-500">Tambahkan satu atau lebih bulan untuk periode ini.</p>
              <div className="flex gap-2">
                <Select value={monthPicker} onValueChange={setMonthPicker}>
                  <SelectTrigger className="flex-1" data-testid="month-picker-trigger">
                    <SelectValue placeholder="Pilih bulan..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {allMonthOptions.map((opt) => {
                      const alreadyInForm = formData.months.includes(opt.key);
                      const usedElsewhere = usedMonthsByOthers.has(opt.key);
                      const disabled = alreadyInForm || usedElsewhere;
                      return (
                        <SelectItem
                          key={opt.key}
                          value={opt.key}
                          disabled={disabled}
                          data-testid={`month-option-${opt.key}`}
                        >
                          {opt.label}{usedElsewhere ? ' (sudah terpakai)' : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={() => addMonth(monthPicker)}
                  disabled={!monthPicker}
                  data-testid="add-month-btn"
                >
                  <Plus size={16} className="mr-1" /> Tambah
                </Button>
              </div>
              {formData.months.length === 0 ? (
                <p className="text-xs text-red-500">Belum ada bulan dipilih.</p>
              ) : (
                <div className="flex flex-wrap gap-2 pt-2" data-testid="selected-months-list">
                  {formData.months.map((m) => (
                    <Badge key={m} variant="secondary" className="px-3 py-1 text-sm flex items-center gap-1">
                      {formatMonthKey(m)}
                      <button
                        type="button"
                        onClick={() => removeMonth(m)}
                        className="ml-1 rounded-full hover:bg-slate-300 p-0.5"
                        data-testid={`remove-month-${m}`}
                        aria-label={`Hapus ${formatMonthKey(m)}`}
                      >
                        <X size={12} />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="pt-1">
            <Label htmlFor="period-op-cost">Biaya Operasional Default (Rp/gr)</Label>
            <Input
              id="period-op-cost"
              type="number"
              min="0"
              step="1"
              placeholder="Contoh: 100"
              value={formData.default_operational_cost}
              onChange={(e) => setFormData({ ...formData, default_operational_cost: e.target.value })}
              data-testid="period-default-op-cost-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Biaya operasional per gram khusus periode ini — dipakai saat input penjualan &amp; di halaman Rekapan. Mengubah nilai pada periode aktif akan menghitung ulang Biaya Operasional periode tersebut.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setFormDialogOpen(false)}
              disabled={submitting}
            >
              Batal
            </Button>
            <Button
              type="button"
              onClick={handleSubmitPeriod}
              disabled={submitting}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="period-submit-btn"
            >
              {submitting ? 'Menyimpan...' : (editingPeriod ? 'Simpan Perubahan' : 'Buat Periode')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Period Confirmation Dialog */}
      <Dialog
        open={closeConfirmDialogOpen}
        onOpenChange={(open) => {
          setCloseConfirmDialogOpen(open);
          if (!open) {
            setPeriodToClose(null);
            setNextPeriodId('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={24} />
              Konfirmasi Tutup Buku
            </DialogTitle>
            <DialogDescription>
              Proses ini tidak dapat dibatalkan. Pastikan semua data sudah benar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 font-medium mb-2">
                Anda akan menutup periode: <strong>{periodToClose?.name}</strong>
              </p>
              <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
                <li>Semua bulan dalam periode ini akan ikut dikunci</li>
                <li>Transaksi cabang (Pembelian, Biaya, Penjualan) tidak bisa diubah lagi</li>
                <li>Laba/Rugi Mitra akan ditambahkan ke periode berikutnya</li>
                <li>Saldo Investor akan diperbarui sesuai dividen</li>
                <li>Hasil Invest akan ditambahkan ke Laba Rugi Mitra</li>
              </ul>
            </div>

            <div>
              <Label htmlFor="next-period" className="text-sm font-medium">
                Pilih Periode Berikutnya *
              </Label>
              <Select value={nextPeriodId} onValueChange={setNextPeriodId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Pilih periode..." />
                </SelectTrigger>
                <SelectContent>
                  {periods
                    .filter((p) => !p.is_closed && p.id !== periodToClose?.id)
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Transaksi hasil tutup buku akan dicatat di periode ini
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCloseConfirmDialogOpen(false)}
              disabled={submitting}
            >
              Batal
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmClosePeriod}
              disabled={submitting || !nextPeriodId}
            >
              {submitting ? 'Memproses...' : 'Ya, Tutup Buku'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPeriod;
